import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  X, Play, Loader2, ArrowLeftRight, Plus, ChevronDown, Package, Truck, Copy, Check,
  Trash2, TrendingUp, TrendingDown, AlertTriangle, Scissors, Lock,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import axiosInstance from '../utils/axiosInstance';
import ScenarioProductEditor from './ScenarioProductEditor';
import { productTotals, grossMargin, HEALTHY_MARGIN_PCT } from '../utils/orderMargin';

let scenarioCounter = 0;

const makeScenario = (sourceProducts, name) => ({
  id: `sc-${++scenarioCounter}-${Date.now()}`,
  name,
  products: JSON.parse(JSON.stringify(sourceProducts)),
  strategy: null,
  calculating: false,
  error: null,
  selected: true,
});

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const getKey = (p) => p.variant_id || p.id;
const isPalletBin = (bin) => /PLT|PALLET/i.test(bin?.bin_name || '');

/**
 * Metrics of one column. The margin is the simulator's own formula
 * (utils/orderMargin, same as the backend record): value - cost - commission
 * + shipping charged - shipping cost. A scenario keeps the order's shipping
 * charge (the customer paid it once) and carries the commission pro rata to
 * its value; the selected total counts both once.
 */
const columnMetrics = (products, strategy, effectiveCost, ctx) => {
  const t = productTotals(products);
  const bins = strategy?.bins_used || [];
  const pallets = bins.filter(isPalletBin).length;
  const binsWeight = bins.reduce((s, b) => s + (Number(b.actual_weight) || 0), 0);
  const productWeight = products.reduce((s, p) => s + (Number(p.weight) || 0) * (Number(p.quantity) || 0), 0);
  const shipping = strategy ? effectiveCost(strategy) : 0;
  const commission = ctx.baseValue > 0 ? ctx.commission * (t.value / ctx.baseValue) : 0;
  const margin = grossMargin({ value: t.value, cost: t.cost, commission, charged: ctx.charged, shipping });
  return {
    ...t,
    shipping,
    commission,
    parcels: bins.length - pallets,
    pallets,
    bins,
    weight: binsWeight > 0 ? binsWeight : productWeight,
    carriers: [...new Set(bins.map((b) => String(b.carrier_name || '').split(' (')[0]).filter(Boolean))],
    marginPct: t.cost > 0 ? margin.pct : null,
    marginAmount: margin.amount,
  };
};

/**
 * Scenario comparison, in a modal: one visual card per scenario next to the
 * original order, the product editor of the active scenario below.
 */
const ScenarioComparisonPanel = ({ open, baselineOrderData, baselineResult, effectiveCost, onClose }) => {
  const baselineProducts = useMemo(() => baselineOrderData.products || [], [baselineOrderData]);
  const baselineStrategy = baselineResult.strategy;
  const ctx = useMemo(() => ({
    baseValue: productTotals(baselineProducts).value,
    commission: Number(baselineOrderData.professional_commission) || 0,
    charged: Number(baselineOrderData.total_shipping_cost) || 0,
  }), [baselineProducts, baselineOrderData]);

  const [scenarios, setScenarios] = useState(() => [makeScenario(baselineProducts, 'Scenario 1')]);
  const [activeScenarioId, setActiveScenarioId] = useState(() => scenarios[0]?.id);
  const [copied, setCopied] = useState(false);
  const nameCounter = useRef(1);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const activeScenario = scenarios.find((s) => s.id === activeScenarioId) || scenarios[0];

  const updateScenario = useCallback((id, patch) => {
    setScenarios((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  // --- Scenario CRUD ---

  const addScenarios = (list) => {
    setScenarios((prev) => [...prev, ...list]);
    setActiveScenarioId(list[0].id);
  };

  const addScenario = (sourceProducts) => {
    nameCounter.current += 1;
    addScenarios([makeScenario(sourceProducts, `Scenario ${nameCounter.current}`)]);
  };

  // One scenario per order line: "what if each line shipped on its own?"
  const splitPerLine = () => {
    addScenarios(baselineProducts.map((p) => makeScenario([p], (p.title || p.sku || 'Line').slice(0, 28))));
  };

  const removeScenario = (id) => {
    if (scenarios.length <= 1) return;
    const next = scenarios.filter((s) => s.id !== id);
    setScenarios(next);
    if (activeScenarioId === id) setActiveScenarioId(next[0].id);
  };

  const renameScenario = (id, name) => {
    if (name.trim()) updateScenario(id, { name: name.trim() });
  };

  const isChanged = (s) => {
    if (s.products.length !== baselineProducts.length) return true;
    const baseMap = new Map(baselineProducts.map((p) => [getKey(p), p.quantity]));
    return s.products.some((p) => baseMap.get(getKey(p)) !== p.quantity);
  };

  // --- Simulate ---

  const simulate = async (scenario) => {
    const id = scenario.id;
    updateScenario(id, { calculating: true, error: null });
    try {
      const country = baselineOrderData.shipping_address?.country || '';
      const postalCode = baselineOrderData.shipping_address?.zip || '';
      const orderName = (baselineOrderData.order_name || 'CUSTOM').replace(/^#/, '');
      const idx = scenarios.findIndex((s) => s.id === id) + 1;
      const response = await axiosInstance.post(
        `api/orders/${encodeURIComponent(orderName)}-VS${idx}/`,
        { products: scenario.products, shipping_country: country, postal_code: postalCode },
      );
      if (response.data.status === 'success') {
        const solution = response.data.solution;
        // The backend order already puts the strategy ops will ship first
        // (cheapest after cut-box savings, many-parcels-to-pallet swap...),
        // exactly like the simulator page: never re-sort it here.
        const list = Array.isArray(response.data.all_strategies_sorted) && response.data.all_strategies_sorted.length
          ? response.data.all_strategies_sorted
          : [{ strategy: solution?.strategy, total_cost: solution?.total_cost, bins_used: solution?.bins_used }];
        updateScenario(id, { strategy: list[0], calculating: false });
      } else {
        updateScenario(id, { error: response.data.message || 'Simulation failed', calculating: false });
      }
    } catch (err) {
      updateScenario(id, { error: err.response?.data?.message || 'Simulation failed', calculating: false });
    }
  };

  const pending = scenarios.filter((s) => !s.strategy && !s.calculating && s.products.length > 0);
  const simulateAll = () => pending.forEach(simulate);

  // --- Metrics ---

  const baseline = useMemo(
    () => columnMetrics(baselineProducts, baselineStrategy, effectiveCost, ctx),
    [baselineProducts, baselineStrategy, effectiveCost, ctx],
  );
  const metrics = useMemo(
    () => Object.fromEntries(scenarios.map((s) => [
      s.id, s.strategy ? columnMetrics(s.products, s.strategy, effectiveCost, ctx) : null,
    ])),
    [scenarios, effectiveCost, ctx],
  );
  const selected = scenarios.filter((s) => s.selected && metrics[s.id]);
  const total = useMemo(() => {
    if (selected.length < 2) return null;
    const sum = (k) => selected.reduce((a, s) => a + metrics[s.id][k], 0);
    const t = {
      shipping: sum('shipping'), parcels: sum('parcels'), pallets: sum('pallets'), weight: sum('weight'),
      items: sum('items'), value: sum('value'), cost: sum('cost'),
      carriers: [...new Set(selected.flatMap((s) => metrics[s.id].carriers))],
      bins: selected.flatMap((s) => metrics[s.id].bins),
    };
    const m = grossMargin({ value: t.value, cost: t.cost, commission: sum('commission'), charged: ctx.charged, shipping: t.shipping });
    return { ...t, marginPct: t.cost > 0 ? m.pct : null };
  }, [selected, metrics, ctx]);

  const maxShipping = Math.max(
    baseline.shipping,
    total?.shipping || 0,
    ...scenarios.map((s) => metrics[s.id]?.shipping || 0),
    1,
  );

  const unpriced = activeScenario
    ? activeScenario.products.filter((p) => !baselineProducts.some((b) => getKey(b) === getKey(p)) && !(Number(p.unit_price) > 0))
    : [];

  const summaryText = useMemo(() => {
    const done = scenarios.filter((s) => metrics[s.id]);
    if (!done.length) return '';
    const name = baselineOrderData.order_name ? `order ${baselineOrderData.order_name.startsWith('#') ? '' : '#'}${baselineOrderData.order_name}` : 'this order';
    const line = (label, m) => `- ${label}: ${eur(m.shipping)} (${m.parcels} parcel${m.parcels !== 1 ? 's' : ''}${m.pallets ? `, ${m.pallets} pallet${m.pallets !== 1 ? 's' : ''}` : ''})`
      + (m.marginPct != null ? `, margin ${m.marginPct.toFixed(1)}%` : '');
    return [
      `Shipping scenarios for ${name}:`,
      line('As ordered', baseline),
      ...done.map((s) => line(s.name, metrics[s.id])),
      ...(total ? [line(`Selected together (${selected.length})`, total)] : []),
    ].join('\n');
  }, [scenarios, metrics, baseline, total, selected.length, baselineOrderData.order_name]);

  const handleCopy = () => {
    navigator.clipboard.writeText(summaryText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!open) return null;

  const country = baselineOrderData.shipping_address?.country || 'Unknown';
  const postalCode = baselineOrderData.shipping_address?.zip || '';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 backdrop-blur-sm p-2 sm:p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-7xl rounded-xl border border-border bg-card shadow-2xl flex flex-col max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-3rem)]">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-border">
          <ArrowLeftRight className="h-5 w-5 text-primary" />
          <div className="mr-auto">
            <h2 className="text-base font-semibold">Compare scenarios {baselineOrderData.order_name ? `· ${baselineOrderData.order_name}` : ''}</h2>
            <p className="text-xs text-muted-foreground">
              Shipping to {country}{postalCode ? ` ${postalCode}` : ''}. Change the cart, simulate, and compare with the order as placed.
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm"><Plus className="h-4 w-4 mr-1" /> Scenario <ChevronDown className="h-3 w-3 ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">Start from</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => addScenario(baselineProducts)}>
                <span className="text-xs">The order as placed</span>
              </DropdownMenuItem>
              {activeScenario && (
                <DropdownMenuItem onClick={() => addScenario(activeScenario.products)}>
                  <span className="text-xs">A copy of &ldquo;{activeScenario.name}&rdquo;</span>
                </DropdownMenuItem>
              )}
              {baselineProducts.length > 1 && (
                <DropdownMenuItem onClick={splitPerLine}>
                  <Scissors className="h-3.5 w-3.5 mr-2" />
                  <span className="text-xs">Split: one scenario per line ({baselineProducts.length})</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={simulateAll} disabled={pending.length === 0}>
            <Play className="h-4 w-4 mr-1" /> Simulate{pending.length > 1 ? ` all (${pending.length})` : ''}
          </Button>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted text-muted-foreground hover:text-foreground" title="Close (Esc). Your scenarios are kept.">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto">
          {/* Comparison board */}
          <div className="px-5 pt-4 pb-3 bg-muted/30 border-b border-border">
            <div className="flex gap-3 overflow-x-auto pb-1">
              <ScenarioCard
                name="As ordered"
                badge={<span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> baseline</span>}
                m={baseline}
                maxShipping={maxShipping}
              />
              {scenarios.map((s) => (
                <ScenarioCard
                  key={s.id}
                  name={s.name}
                  m={metrics[s.id]}
                  base={baseline}
                  maxShipping={maxShipping}
                  active={s.id === activeScenario?.id}
                  onClick={() => setActiveScenarioId(s.id)}
                  onRename={(n) => renameScenario(s.id, n)}
                  onRemove={scenarios.length > 1 ? () => removeScenario(s.id) : null}
                  selected={s.selected}
                  onToggleSelected={() => updateScenario(s.id, { selected: !s.selected })}
                  calculating={s.calculating}
                  error={s.error}
                  changed={isChanged(s)}
                  itemCount={productTotals(s.products).items}
                  onSimulate={() => simulate(s)}
                />
              ))}
              {total && (
                <ScenarioCard
                  name={`Selected together (${selected.length})`}
                  badge="ticked scenarios, summed"
                  m={total}
                  base={baseline}
                  maxShipping={maxShipping}
                  isTotal
                />
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Margin = order value − product cost − commission + shipping charged ({eur(ctx.charged)}) − shipping cost, the same formula as the
              simulator and the order records. A scenario keeps the charge the customer paid and its share of the commission; the green line marks {HEALTHY_MARGIN_PCT}%.
            </p>
          </div>

          {/* Active scenario editor */}
          {activeScenario && (
            <div className="px-5 py-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="text-sm font-semibold">Editing: {activeScenario.name}</h3>
                {!isChanged(activeScenario) && (
                  <span className="text-xs text-muted-foreground">Same cart as the order. Add, remove or change quantities to compare.</span>
                )}
                <Button size="sm" className="ml-auto" onClick={() => simulate(activeScenario)}
                  disabled={activeScenario.calculating || activeScenario.products.length === 0}>
                  {activeScenario.calculating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                  {activeScenario.calculating ? 'Simulating...' : activeScenario.strategy ? 'Simulate again' : 'Simulate this scenario'}
                </Button>
              </div>
              {unpriced.length > 0 && (
                <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {unpriced.length} added product{unpriced.length !== 1 ? 's have' : ' has'} no price yet: fill price and cost in the table, or the margin leaves them out.
                </div>
              )}
              {activeScenario.error && (
                <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-3 py-2 text-sm text-red-800 dark:text-red-200">
                  {activeScenario.error}
                </div>
              )}
              <ScenarioProductEditor
                products={activeScenario.products}
                onProductsChange={(newProducts) => updateScenario(activeScenario.id, { products: newProducts, strategy: null, error: null })}
                baselineProducts={baselineProducts}
              />
            </div>
          )}
        </div>

        {/* Footer: copyable summary */}
        {summaryText && (
          <div className="flex items-start gap-2 border-t border-border bg-muted/30 px-5 py-3">
            <pre className="text-xs text-muted-foreground flex-1 whitespace-pre-wrap font-sans max-h-24 overflow-y-auto">{summaryText}</pre>
            <Button variant="outline" size="sm" className="shrink-0" onClick={handleCopy}>
              {copied ? <Check className="h-3.5 w-3.5 mr-1 text-emerald-500" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const Delta = ({ value, unit = '€', invert = false, precision = 2 }) => {
  if (value == null || Math.abs(value) < 0.005) {
    return <span className="text-[11px] text-muted-foreground">same as order</span>;
  }
  const good = invert ? value > 0 : value < 0;
  const Icon = value > 0 ? TrendingUp : TrendingDown;
  const txt = unit === '€' ? `${value > 0 ? '+' : '−'}€${Math.abs(value).toFixed(precision)}` : `${value > 0 ? '+' : '−'}${Math.abs(value).toFixed(precision)}${unit}`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${good ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
      <Icon className="h-3 w-3" /> {txt}
    </span>
  );
};

/** Parcels and pallets as icons, so the shape of the shipment reads at a glance. */
const ShipmentIcons = ({ bins }) => {
  const MAX = 14;
  const shown = bins.slice(0, MAX);
  return (
    <div className="flex flex-wrap items-center gap-0.5 min-h-[20px]">
      {shown.map((b, i) => (isPalletBin(b)
        ? <Truck key={i} className="h-4 w-4 text-amber-600 dark:text-amber-400" title={b.bin_name} />
        : <Package key={i} className="h-4 w-4 text-sky-600 dark:text-sky-400" title={b.bin_name} />))}
      {bins.length > MAX && <span className="text-[11px] text-muted-foreground ml-0.5">+{bins.length - MAX}</span>}
    </div>
  );
};

/** Margin bar, 0 to 40%, with the healthy line. */
const MarginGauge = ({ pct }) => {
  if (pct == null) return <p className="text-[11px] text-muted-foreground">Margin: no product cost</p>;
  const ok = pct >= HEALTHY_MARGIN_PCT;
  const width = Math.max(0, Math.min(100, (pct / 40) * 100));
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] text-muted-foreground">Margin</span>
        <span className={`text-sm font-bold font-mono ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{pct.toFixed(1)}%</span>
      </div>
      <div className="relative h-2 rounded-full bg-muted mt-1">
        <div className={`h-2 rounded-full ${ok ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${width}%` }} />
        <div className="absolute top-[-2px] h-3 w-0.5 bg-emerald-700 dark:bg-emerald-300" style={{ left: `${(HEALTHY_MARGIN_PCT / 40) * 100}%` }} />
      </div>
    </div>
  );
};

const ScenarioCard = ({
  name, badge, m, base, maxShipping, active, onClick, onRename, onRemove, selected, onToggleSelected,
  calculating, error, changed, itemCount, onSimulate, isTotal,
}) => {
  const frame = isTotal
    ? 'border-primary/40 bg-primary/5'
    : active ? 'border-primary ring-2 ring-primary/30 bg-card' : 'border-border bg-card hover:border-primary/40';
  return (
    <div onClick={onClick}
      className={`w-60 shrink-0 rounded-xl border p-3 flex flex-col gap-2 transition-colors ${frame} ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center gap-1.5 min-h-[20px]">
        {onToggleSelected && (
          <input type="checkbox" checked={selected} onClick={(e) => e.stopPropagation()} onChange={onToggleSelected}
            className="h-3.5 w-3.5 rounded accent-primary" title="Tick to add this scenario to the 'Selected together' total" />
        )}
        {onRename ? <EditableName name={name} onRename={onRename} /> : <span className="text-sm font-semibold truncate">{name}</span>}
        {onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Delete scenario"
            className="ml-auto rounded p-0.5 text-muted-foreground hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {badge && <p className="text-[11px] text-muted-foreground -mt-1.5">{badge}</p>}

      {calculating ? (
        <div className="flex-1 flex items-center justify-center py-8 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Simulating...
        </div>
      ) : !m ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 py-5 text-center">
          <p className="text-xs text-muted-foreground">
            {error ? <span className="text-red-600">{error}</span> : changed ? `${itemCount} items, not simulated yet` : 'Same cart as the order'}
          </p>
          {changed && (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onSimulate(); }}>
              <Play className="h-3.5 w-3.5 mr-1" /> Simulate
            </Button>
          )}
        </div>
      ) : (
        <>
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-2xl font-bold font-mono">{eur(m.shipping)}</span>
              {base && <Delta value={m.shipping - base.shipping} />}
            </div>
            <div className="h-1.5 rounded-full bg-muted mt-1.5">
              <div className="h-1.5 rounded-full bg-primary/70" style={{ width: `${Math.max(2, (m.shipping / maxShipping) * 100)}%` }} />
            </div>
          </div>
          <ShipmentIcons bins={m.bins} />
          <p className="text-[11px] text-muted-foreground leading-snug">
            {m.parcels} parcel{m.parcels !== 1 ? 's' : ''}{m.pallets ? ` · ${m.pallets} pallet${m.pallets !== 1 ? 's' : ''}` : ''} · {m.weight.toFixed(1)} kg · {m.items} item{m.items !== 1 ? 's' : ''}
            {m.carriers.length > 0 && <><br />via {m.carriers.join(' + ')}</>}
          </p>
          <MarginGauge pct={m.marginPct} />
          {base && m.marginPct != null && base.marginPct != null && (
            <div className="-mt-1"><Delta value={m.marginPct - base.marginPct} unit=" pp" precision={1} invert /></div>
          )}
          {!isTotal && <p className="text-[11px] text-muted-foreground">Order value {eur(m.value)}</p>}
        </>
      )}
    </div>
  );
};

/** Double-click to rename. */
const EditableName = ({ name, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  if (!editing) {
    return (
      <span onDoubleClick={(e) => { e.stopPropagation(); setDraft(name); setEditing(true); }}
        className="text-sm font-semibold truncate" title="Double-click to rename">
        {name}
      </span>
    );
  }
  const commit = () => { onRename(draft); setEditing(false); };
  return (
    <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); } }}
      onClick={(e) => e.stopPropagation()}
      className="bg-transparent border-b border-primary outline-none text-sm font-semibold w-36 px-0 py-0" />
  );
};

export default ScenarioComparisonPanel;
